import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { DeleteConfirmationModal } from '../components/common/DeleteConfirmationModal';
import { useToast } from '../contexts/ToastContext';

export const AdminContent: React.FC = () => {
  const { darkMode } = useTheme();
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'personas' | 'sessions' | 'artifacts'>('personas');
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Fetch personas
  const { 
    data: personas, 
    isLoading: personasLoading, 
    refetch: refetchPersonas 
  } = useQuery(
    ['adminPersonas'],
    async () => {
      const response = await api.get('/api/personas');
      return response.data;
    },
    {
      enabled: activeTab === 'personas',
    }
  );
  
  // Fetch sessions
  const { 
    data: sessions, 
    isLoading: sessionsLoading, 
    refetch: refetchSessions 
  } = useQuery(
    ['adminSessions'],
    async () => {
      const response = await api.get('/api/sessions');
      return response.data;
    },
    {
      enabled: activeTab === 'sessions',
    }
  );
  
  // Fetch artifacts
  const { 
    data: artifacts, 
    isLoading: artifactsLoading, 
    refetch: refetchArtifacts 
  } = useQuery(
    ['adminArtifacts'],
    async () => {
      const response = await api.get('/api/artifacts');
      return response.data;
    },
    {
      enabled: activeTab === 'artifacts',
    }
  );
  
  const handleDeleteClick = (item: any) => {
    setItemToDelete(item);
  };
  
  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    setIsDeleting(true);
    try {
      if (activeTab === 'personas') {
        await api.delete(`/api/personas/${itemToDelete.id}`);
        refetchPersonas();
      } else if (activeTab === 'sessions') {
        await api.delete(`/api/sessions/${itemToDelete.id}`);
        refetchSessions();
      } else if (activeTab === 'artifacts') {
        await api.delete(`/api/artifacts/${itemToDelete.id}`);
        refetchArtifacts();
      }
      
      showToast(`${activeTab.slice(0, -1)} deleted successfully`, 'success');
    } catch (error) {
      console.error(`Error deleting ${activeTab.slice(0, -1)}:`, error);
      showToast(`Failed to delete ${activeTab.slice(0, -1)}`, 'error');
    } finally {
      setIsDeleting(false);
      setItemToDelete(null);
    }
  };
  
  const renderContent = () => {
    if (activeTab === 'personas') {
      return (
        <div>
          <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Manage Personas
          </h2>
          
          {personasLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Created
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y divide-gray-200 dark:divide-gray-700 ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>
                  {personas?.map((persona) => (
                    <tr key={persona.id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {persona.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {persona.primarySpecialization || 'Standard'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(persona.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleDeleteClick(persona)}
                          className="px-2 py-1 rounded-md bg-red-600 hover:bg-red-700 text-white"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    }
    
    if (activeTab === 'sessions') {
      return (
        <div>
          <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Manage Sessions
          </h2>
          
          {sessionsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Phase
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Created
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y divide-gray-200 dark:divide-gray-700 ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>
                  {sessions?.map((session) => (
                    <tr key={session.id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {session.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {session.phase || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(session.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleDeleteClick(session)}
                          className="px-2 py-1 rounded-md bg-red-600 hover:bg-red-700 text-white"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    }
    
    if (activeTab === 'artifacts') {
      return (
        <div>
          <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Manage Artifacts
          </h2>
          
          {artifactsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Session
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Created
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y divide-gray-200 dark:divide-gray-700 ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>
                  {artifacts?.map((artifact) => (
                    <tr key={artifact.id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {artifact.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {artifact.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {artifact.session_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(artifact.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleDeleteClick(artifact)}
                          className="px-2 py-1 rounded-md bg-red-600 hover:bg-red-700 text-white"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    }
    
    return null;
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className={`text-2xl font-bold mb-6 ${darkMode ? 'text-red-300 border-l-4 border-red-700 pl-3' : 'text-red-700 border-l-4 border-red-500 pl-3'}`}>
        Content Management
      </h1>
      
      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('personas')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'personas'
                ? darkMode 
                  ? 'border-blue-500 text-blue-400' 
                  : 'border-blue-500 text-blue-600'
                : darkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Personas
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'sessions'
                ? darkMode 
                  ? 'border-blue-500 text-blue-400' 
                  : 'border-blue-500 text-blue-600'
                : darkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Sessions
          </button>
          <button
            onClick={() => setActiveTab('artifacts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'artifacts'
                ? darkMode 
                  ? 'border-blue-500 text-blue-400' 
                  : 'border-blue-500 text-blue-600'
                : darkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Artifacts
          </button>
        </nav>
      </div>
      
      {/* Content */}
      <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {renderContent()}
      </div>
      
      {/* Delete confirmation modal */}
      <DeleteConfirmationModal
        isOpen={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        onConfirm={confirmDelete}
        itemType={activeTab.slice(0, -1)}
        itemName={itemToDelete?.name || itemToDelete?.id}
        isDeleting={isDeleting}
        dangerLevel="medium"
      />
    </div>
  );
};

export default AdminContent;
